import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  useTheme,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  TextField,
  TablePagination,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import authService from "../services/auth";
// Users Panel Component
const UsersPanel = () => {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userScans, setUserScans] = useState({ scanCount: 0, resumeNames: [] });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${API_URL}/api/monitoring/users`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}` }
        });
        if (res.ok) {
          const json = await res.json();
          if (json.status === 'success') {
            setUsers(Array.isArray(json.data) ? json.data : []);
          }
        }
      } catch (e) {
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${API_URL}/api/monitoring/users/${user.id}/scans`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}` }
      });
      if (res.ok) {
        const json = await res.json();
        if (json.status === 'success') {
          setUserScans(json.data || { scanCount: 0, resumeNames: [] });
        }
      }
    } catch (e) {}
  };

  return (
    <Card>
      <CardHeader title="Users" />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Total Scans</TableCell>
                      <TableCell>Last Login</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} hover onClick={() => handleSelectUser(u)} sx={{ cursor: 'pointer' }}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                              {String(u.email || '').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">{u.email}</Typography>
                              <Typography variant="caption" color="text.secondary">{`${u.firstName || ''} ${u.lastName || ''}`.trim()}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{u.totalScans}</TableCell>
                        <TableCell>
                          {u.lastLogin ? new Date(u.lastLogin).toLocaleString('en-US', { timeZone: 'America/New_York' }) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {selectedUser ? (
                  <>
                    <Typography variant="h6" gutterBottom>
                      {selectedUser.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total scans: {userScans.scanCount}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>Resume Names</Typography>
                    {userScans.resumeNames && userScans.resumeNames.length > 0 ? (
                      <List dense>
                        {userScans.resumeNames.map((name, idx) => (
                          <ListItem key={`${name}-${idx}`}>
                            <ListItemText primary={name} />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No resumes found.</Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">Select a user to view details.</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, trend, icon, color, subtitle, loading = false }) => {
  const theme = useTheme();
  
  const getTrendIcon = () => {
    if (trend > 0) return <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />;
    if (trend < 0) return <TrendingDownIcon sx={{ color: theme.palette.error.main, fontSize: 16 }} />;
    return <InfoIcon sx={{ color: theme.palette.info.main, fontSize: 16 }} />;
  };

  const getTrendColor = () => {
    if (trend > 0) return theme.palette.success.main;
    if (trend < 0) return theme.palette.error.main;
    return theme.palette.info.main;
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Avatar 
            sx={{ 
              bgcolor: color || theme.palette.primary.main,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
          {trend !== undefined && (
            <Box display="flex" alignItems="center" gap={0.5}>
              {getTrendIcon()}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: getTrendColor(),
                  fontWeight: 600,
                }}
              >
                {Math.abs(trend)}%
              </Typography>
            </Box>
          )}
        </Box>
        
        <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 0.5 }}>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            value
          )}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {title}
        </Typography>
        
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Activity Timeline Component
const ActivityTimeline = ({ data, loading = false }) => {
  const theme = useTheme();
  const [timelineData, setTimelineData] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);

  useEffect(() => {
    const fetchTimelineData = async () => {
      try {
        setTimelineLoading(true);
        const API_URL = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${API_URL}/api/monitoring/timeline`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          // Expected shape: { status: 'success', data: { timeline: [...] } }
          if (result.status === 'success') {
            const dataArray = Array.isArray(result.data)
              ? result.data
              : Array.isArray(result.data?.timeline)
                ? result.data.timeline
                : [];
            setTimelineData(dataArray);
          } else {
            setTimelineData([]);
          }
        } else {
          setTimelineData([]);
        }
      } catch (error) {
      } finally {
        setTimelineLoading(false);
      }
    };

    fetchTimelineData();
  }, []);
  
  if (loading || timelineLoading) {
    return (
      <Card>
        <CardHeader title="Activity Timeline" />
        <CardContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Build a stable 7-day window and map backend data by YYYY-MM-DD keys
  const buildChartData = (raw) => {
    if (!Array.isArray(raw)) return [];

    const keyFromItem = (it) => {
      if (typeof it?._id === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(it._id)) return it._id;
      if (typeof it?.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(it.date)) return it.date;
      return null;
    };

    const countsFromActivities = (it) => {
      const activities = Array.isArray(it?.activities) ? it.activities : [];
      const sum = (pred) => activities.reduce((acc, a) => (pred(a?.action) ? acc + (a?.count || 0) : acc), 0);
      const logins = sum((act) => {
        const a = String(act || '').toLowerCase();
        return a.startsWith('login_success') || a === '2fa_success' || a === 'login_success_2fa_bypass';
      });
      const logouts = sum((act) => String(act || '') === 'logout');
      return { logins, logouts };
    };

    const byKey = new Map();
    for (const it of raw) {
      const key = keyFromItem(it);
      if (!key) continue;
      byKey.set(key, countsFromActivities(it));
    }

    // Generate last 7 days (including today) in UTC
    const days = [];
    const todayUtc = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), todayUtc.getUTCDate()));
      d.setUTCDate(d.getUTCDate() - i);
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      const key = `${y}-${m}-${day}`;
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const counts = byKey.get(key) || { logins: 0, logouts: 0 };
      days.push({ date: label, ...counts, total: counts.logins + counts.logouts });
    }

    return days;
  };

  const chartData = buildChartData(timelineData);

  return (
    <Card>
      <CardHeader 
        title="Activity Timeline (Last 7 Days)"
        action={
          <Chip 
            label="Live" 
            color="success" 
            size="small" 
            icon={<CheckCircleIcon />}
          />
        }
      />
      <CardContent>
        {chartData && chartData.length > 0 ? (
          <Box height={300}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: theme.palette.text.secondary }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: theme.palette.text.secondary }}
                />
                <RechartsTooltip 
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.shape.borderRadius
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="logins" 
                  stroke={theme.palette.primary.main} 
                  strokeWidth={2}
                  name="Logins"
                  dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                />
                {/* Scans series removed per request */}
                <Line 
                  type="monotone" 
                  dataKey="logouts" 
                  stroke={theme.palette.error.main} 
                  strokeWidth={2}
                  name="Logouts"
                  dot={{ fill: theme.palette.error.main, strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box height={300} display="flex" alignItems="center" justifyContent="center">
            <Typography variant="body2" color="text.secondary">
              {timelineLoading ? 'Loading timeline data...' : 'No activity data available'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Recent Activity Table Component
const RecentActivityTable = ({ data, loading = false }) => {
  const theme = useTheme();
  const [filterEmail, setFilterEmail] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [displayedData, setDisplayedData] = useState(Array.isArray(data) ? data : []);
  const [page, setPage] = useState(0); // 0-based for UI
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const arr = Array.isArray(data) ? data : [];
    setDisplayedData(arr);
    setTotalCount(Array.isArray(arr) ? arr.length : 0);
  }, [data]);

  // Initial fetch with pagination
  useEffect(() => {
    handleApplyFilters(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = async (newPage) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const params = new URLSearchParams();
      if (filterEmail) params.append('email', filterEmail);
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);
      const effectivePage = typeof newPage === 'number' ? newPage : page;
      params.append('page', String(effectivePage + 1)); // backend is 1-based
      params.append('limit', String(rowsPerPage));

      const res = await fetch(`${API_URL}/api/monitoring/activity?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}`
        }
      });
      if (!res.ok) {
        return;
      }
      const json = await res.json();
      if (json.status === 'success') {
        const rows = Array.isArray(json.data) ? json.data : Array.isArray(json.data?.rows) ? json.data.rows : [];
        setDisplayedData(rows);
        const total = json.pagination?.total ?? rows.length;
        setTotalCount(total);
        setPage(effectivePage);
      }
    } catch (e) {
    }
  };

  const handleChangePage = (_evt, newPage) => {
    handleApplyFilters(newPage);
  };

  const handleChangeRowsPerPage = (evt) => {
    const newSize = parseInt(evt.target.value, 10) || 25;
    setRowsPerPage(newSize);
    setPage(0);
    // refetch from first page with new page size
    handleApplyFilters(0);
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />;
      case 'error':
        return <CancelIcon sx={{ color: theme.palette.error.main, fontSize: 16 }} />;
      case 'warning':
        return <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 16 }} />;
      default:
        return <InfoIcon sx={{ color: theme.palette.info.main, fontSize: 16 }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return theme.palette.info.main;
    }
  };

  const handleExport = () => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    // Convert data to CSV format
    const headers = ['Time', 'User', 'Action', 'IP Address', 'Status'];
    const csvContent = [
      headers.join(','),
      ...data.map(item => [
        item.time || '',
        item.user || '',
        item.action || '',
        item.ip || '',
        item.status || ''
      ].map(field => `"${field}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `activity-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader title="Recent Activity" />
        <CardContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader 
        title="Recent User Activity"
        action={
          <Button 
            startIcon={<DownloadIcon />} 
            size="small"
            variant="outlined"
            onClick={handleExport}
          >
            Export
          </Button>
        }
      />
      <Box sx={{ px: 2, pb: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Filter by User (email)"
              placeholder="user@example.com"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Start date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="End date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button fullWidth size="small" variant="outlined" onClick={handleApplyFilters}>
              Apply
            </Button>
          </Grid>
        </Grid>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedData.map((activity, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  <Typography variant="body2">
                    {activity.date || ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {activity.time}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                      {activity.user.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2">
                      {activity.user}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={activity.action} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {activity.ip}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {activity.location || 'Unknown'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {getStatusIcon(activity.status)}
                    <Typography 
                      variant="body2" 
                      sx={{ color: getStatusColor(activity.status) }}
                    >
                      {activity.status}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="flex-end">
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Box>
    </Card>
  );
};

// System Health Component
const SystemHealthPanel = ({ data, loading = false }) => {
  const theme = useTheme();
  
  if (loading) {
    return (
      <Card>
        <CardHeader title="System Health" />
        <CardContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader 
        title="System Performance"
        action={
          <Chip 
            label="Healthy" 
            color="success" 
            size="small" 
            icon={<CheckCircleIcon />}
          />
        }
      />
      <CardContent>
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            API Response Times
          </Typography>
          {data.apiResponseTimes?.map((api, index) => (
            <Box key={index} mb={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">{api.endpoint}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {api.time}ms
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={Math.min((api.time / 1000) * 100, 100)} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: api.time > 500 ? theme.palette.error.main : 
                                   api.time > 200 ? theme.palette.warning.main : 
                                   theme.palette.success.main,
                  }
                }}
              />
            </Box>
          ))}
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Error Rates by Endpoint
          </Typography>
          {data.errorRates?.map((endpoint, index) => (
            <Box key={index} display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">{endpoint.name}</Typography>
              <Typography 
                variant="body2" 
                color={endpoint.rate > 5 ? 'error.main' : 'success.main'}
              >
                {endpoint.rate}%
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              RAM Occupied
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Heap: {data.memory?.heapUsedMb || 0}MB / {data.memory?.heapTotalMb || 0}MB
            </Typography>
            <Typography variant="body2" color="text.secondary">
              RSS: {data.memory?.rssMb || 0}MB
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Storage Occupied
            </Typography>
            <Typography variant="body2" color="text.secondary">
              DB Data: {Math.round((data.storage?.databaseSizeBytes || 0) / 1024 / 1024)}MB
            </Typography>
            <Typography variant="body2" color="text.secondary">
              DB Storage: {Math.round((data.storage?.storageSizeBytes || 0) / 1024 / 1024)}MB
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Business Intelligence Component
const BusinessIntelligencePanel = ({ data, loading = false }) => {
  const theme = useTheme();
  
  if (loading) {
    return (
      <Card>
        <CardHeader title="Business Intelligence" />
        <CardContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Business Insights" />
      <CardContent>
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Top Job Titles Analyzed
          </Typography>
          <List dense>
            {data.topJobTitles?.map((job, index) => (
              <ListItem key={index} disablePadding>
                <ListItemText
                  primary={
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{job.title}</Typography>
                      <Chip 
                        label={`${job.count} scans`} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={`${job.percentage}% of total scans`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            User Engagement
          </Typography>
          <List dense>
            <ListItem disablePadding>
              <ListItemIcon>
                <PeopleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Average scans per user"
                secondary={data.avgScansPerUser || '3.2'}
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon>
                <ScheduleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Most active hour"
                secondary={data.mostActiveHour || '2:00 PM - 3:00 PM'}
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon>
                <TrendingUpIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Return user rate"
                secondary={data.returnUserRate || '67%'}
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            LLM Monthly Usage (UTC)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tokens: {data.llmUsageMonthUtc?.totalTokens || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cost: ${Number(data.llmUsageMonthUtc?.totalCostUsd || 0).toFixed(4)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cost per token: ${Number(data.llmUsageMonthUtc?.costPerTokenUsd || 0).toFixed(8)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Common Questions (Prompt Frequency)
          </Typography>
          <List dense>
            {(data.commonQuestions || []).slice(0, 5).map((question, index) => (
              <ListItem key={`${question.promptHash}-${index}`} disablePadding>
                <ListItemText
                  primary={question.promptPreview || 'No preview'}
                  secondary={`Count: ${question.count || 0}`}
                />
              </ListItem>
            ))}
            {(!data.commonQuestions || data.commonQuestions.length === 0) && (
              <ListItem disablePadding>
                <ListItemText primary="No prompt metrics available yet." />
              </ListItem>
            )}
          </List>
        </Box>
      </CardContent>
    </Card>
  );
};

// Main Monitoring Dashboard Component
const MonitoringDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [error, setError] = useState('');

  // Real data from API - no mock data
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      activeUsers: 0,
      usersAdded: 0,
      scansToday: 0,
      avgResponseTime: 0,
      errorRate: 0,
      loginEvents: 0,
      logoutEvents: 0,
      profilesScanned: 0,
      unidentifiedUsers: 0,
      avgSessionDuration: 0,
      averageChatWindowSessionTime: 0,
      successRate: 0
    },
    recentActivity: [],
    systemHealth: {
      apiResponseTimes: [],
      errorRates: [],
      memory: {},
      storage: {}
    },
    businessIntelligence: {
      topJobTitles: [],
      avgScansPerUser: '0',
      mostActiveHour: 'No data',
      returnUserRate: '0%',
      llmUsageMonthUtc: {
        totalTokens: 0,
        totalCostUsd: 0,
        costPerTokenUsd: 0
      },
      commonQuestions: []
    }
  });

  // Load data from API
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load all dashboard data in parallel
        const API_URL = process.env.REACT_APP_API_URL || '';
        const [overviewRes, activityRes, healthRes, businessRes] = await Promise.all([
          fetch(`${API_URL}/api/monitoring/overview`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}`
            }
          }),
          fetch(`${API_URL}/api/monitoring/activity`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}`
            }
          }),
          fetch(`${API_URL}/api/monitoring/health`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}`
            }
          }),
          fetch(`${API_URL}/api/monitoring/business`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}`
            }
          })
        ]);

        // Check if responses are ok before parsing JSON
        if (!overviewRes.ok) {
          const errorText = await overviewRes.text();
          throw new Error(`Overview API failed: ${overviewRes.status} - ${errorText}`);
        }
        if (!activityRes.ok) {
          const errorText = await activityRes.text();
          throw new Error(`Activity API failed: ${activityRes.status} - ${errorText}`);
        }
        if (!healthRes.ok) {
          const errorText = await healthRes.text();
          throw new Error(`Health API failed: ${healthRes.status} - ${errorText}`);
        }
        if (!businessRes.ok) {
          const errorText = await businessRes.text();
          throw new Error(`Business API failed: ${businessRes.status} - ${errorText}`);
        }

        const [overview, activity, health, business] = await Promise.all([
          overviewRes.json(),
          activityRes.json(),
          healthRes.json(),
          businessRes.json()
        ]);

        if (overview.status === 'success') {
          setDashboardData(prev => ({
            ...prev,
            metrics: overview.data
          }));
        }

        if (activity.status === 'success') {
          setDashboardData(prev => ({
            ...prev,
            recentActivity: activity.data
          }));
        }

        if (health.status === 'success') {
          setDashboardData(prev => ({
            ...prev,
            systemHealth: health.data
          }));
        }

        if (business.status === 'success') {
          setDashboardData(prev => ({
            ...prev,
            businessIntelligence: business.data
          }));
        }

      } catch (error) {
        // Show error state instead of mock data
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
        setLastUpdated(new Date());
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const startedAt = Date.now();
    const API_URL = process.env.REACT_APP_API_URL || '';
    const token = localStorage.getItem('novamatch_auth_token');

    const sendSessionMetric = () => {
      const durationMs = Date.now() - startedAt;
      if (!token || durationMs < 5000) return;

      fetch(`${API_URL}/api/monitoring/session-metric`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          durationMs,
          route: '/monitoring',
          source: 'frontend'
        }),
        keepalive: true
      }).catch(() => {});
    };

    window.addEventListener('beforeunload', sendSessionMetric);
    return () => {
      sendSessionMetric();
      window.removeEventListener('beforeunload', sendSessionMetric);
    };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      // Reload all dashboard data
      const API_URL = process.env.REACT_APP_API_URL || '';
      const [overviewRes, activityRes, healthRes, businessRes] = await Promise.all([
        fetch(`${API_URL}/api/monitoring/overview`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}`
          }
        }),
        fetch(`${API_URL}/api/monitoring/activity`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}`
          }
        }),
        fetch(`${API_URL}/api/monitoring/health`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}`
          }
        }),
        fetch(`${API_URL}/api/monitoring/business`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('novamatch_auth_token')}`
          }
        })
      ]);

      // Check if responses are ok before parsing JSON
      if (!overviewRes.ok) {
        const errorText = await overviewRes.text();
        throw new Error(`Overview API failed: ${overviewRes.status} - ${errorText}`);
      }
      if (!activityRes.ok) {
        const errorText = await activityRes.text();
        throw new Error(`Activity API failed: ${activityRes.status} - ${errorText}`);
      }
      if (!healthRes.ok) {
        const errorText = await healthRes.text();
        throw new Error(`Health API failed: ${healthRes.status} - ${errorText}`);
      }
      if (!businessRes.ok) {
        const errorText = await businessRes.text();
        throw new Error(`Business API failed: ${businessRes.status} - ${errorText}`);
      }

      const [overview, activity, health, business] = await Promise.all([
        overviewRes.json(),
        activityRes.json(),
        healthRes.json(),
        businessRes.json()
      ]);

      if (overview.status === 'success') {
        setDashboardData(prev => ({
          ...prev,
          metrics: overview.data
        }));
      }

      if (activity.status === 'success') {
        setDashboardData(prev => ({
          ...prev,
          recentActivity: activity.data
        }));
      }

      if (health.status === 'success') {
        setDashboardData(prev => ({
          ...prev,
          systemHealth: health.data
        }));
      }

      if (business.status === 'success') {
        setDashboardData(prev => ({
          ...prev,
          businessIntelligence: business.data
        }));
      }

    } catch (error) {
    } finally {
      setRefreshing(false);
      setLastUpdated(new Date());
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  const handleDashboardExport = () => {
    // Export all dashboard data as JSON
    const exportData = {
      timestamp: new Date().toISOString(),
      metrics: dashboardData.metrics,
      recentActivity: dashboardData.recentActivity,
      systemHealth: dashboardData.systemHealth,
      businessIntelligence: dashboardData.businessIntelligence
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `dashboard-export-${new Date().toISOString().split('T')[0]}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const { metrics } = dashboardData;

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: theme.palette.background.default,
      pb: 4
    }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        py: 2,
        mb: 3
      }}>
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <DashboardIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                  NovaMatch Monitoring Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Real-time system monitoring and analytics
                </Typography>
              </Box>
            </Box>
            
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="caption" color="text.secondary">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </Typography>
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
              <Button 
                variant="outlined" 
                startIcon={<DownloadIcon />}
                size="small"
                onClick={handleDashboardExport}
              >
                Export
              </Button>
              
              <Button 
                variant="outlined" 
                onClick={handleLogout}
                size="small"
              >
                Logout
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl">
        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {/* Real-time Overview Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Active Users"
              value={metrics.activeUsers}
              trend={15}
              icon={<PeopleIcon />}
              color={theme.palette.success.main}
              subtitle="Last 24 hours"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Scans Today"
              value={metrics.scansToday}
              trend={23}
              icon={<AssessmentIcon />}
              color={theme.palette.primary.main}
              subtitle="Total completed"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Avg Response Time"
              value={`${metrics.avgResponseTime}s`}
              trend={-5}
              icon={<SpeedIcon />}
              color={theme.palette.info.main}
              subtitle="API performance"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Error Rate"
              value={`${metrics.errorRate}%`}
              trend={-0.5}
              icon={<ErrorIcon />}
              color={theme.palette.error.main}
              subtitle="Last hour"
              loading={loading}
            />
          </Grid>
        </Grid>

        {/* Key Metrics Row */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Key Metrics (Last 24h)" />
              <CardContent>
                <Grid container spacing={4}>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box textAlign="center">
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                        {metrics.loginEvents}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Login Events
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box textAlign="center">
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                        {metrics.logoutEvents}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Logout Events
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box textAlign="center">
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                        {metrics.profilesScanned}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Profiles Scanned
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box textAlign="center">
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                        {metrics.usersAdded}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Users Added (MTD)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box textAlign="center">
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                        {metrics.averageChatWindowSessionTime || metrics.avgSessionDuration}m
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg Chat Session
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box textAlign="center">
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                        {metrics.successRate}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Success Rate
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs for different views */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Overview" />
            <Tab label="User Analytics" />
            <Tab label="System Health" />
            <Tab label="Business Intelligence" />
            <Tab label="Users" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <ActivityTimeline data={dashboardData} loading={loading} />
            </Grid>
            <Grid item xs={12} lg={4}>
              <RecentActivityTable data={dashboardData.recentActivity} loading={loading} />
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <RecentActivityTable data={dashboardData.recentActivity} loading={loading} />
            </Grid>
          </Grid>
        )}

        {activeTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SystemHealthPanel data={dashboardData.systemHealth} loading={loading} />
            </Grid>
          </Grid>
        )}

        {activeTab === 3 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <BusinessIntelligencePanel data={dashboardData.businessIntelligence} loading={loading} />
            </Grid>
          </Grid>
        )}

        {activeTab === 4 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <UsersPanel />
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default MonitoringDashboard;
